import React, { useEffect, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { Theme, ThemeProvider as ThemeProviderV4 } from "@material-ui/core";
import { ThemeProvider as ThemeProviderV5 } from "@mui/material/styles";
import AppContainer from "./external/ensolvers-core-frontend-mui/components/Main/AppContainer";
import SnackbarComponent from "./external/ensolvers-core-frontend-mui/components/Main/SnackbarComponent";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import authService, { AuthEvents, LoginError } from "./external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import AppNavigation from "./components/AppNavigation";
import { STATIC_ICONS, getLandingPageByUser, getSections } from "./utils/NavigationUtils";
import User, { UserEvents } from "./external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/User";
import NotificationService, { NotificationEvents } from "./external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { SnackBarData } from "./external/ensolvers-core-frontend-mui/types/SnackbarData";
import { sendSnackBarMessage } from "./utils/SnackBarUtils";
import { ColorUtils } from "./utils/ColorUtils";
import featureToggleList from "./utils/toggleConfig.json";
import { useFeatureToggle } from "./external/ensolvers-core-frontend-mui/hooks/useFeatureToggle";
import { Features } from "./utils/Features";
import OpsUserService from "./services/OpsUserService";
import AuthService from "./external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import { themeV4, themeV5 } from "./utils/AppThemeStyle";
import { STORAGE_KEYS } from "./hooks/useLocalStorage";
import useUserInfo from "./hooks/useUserInfo";
import { ToolBar } from "./components/Main/ToolBar";
import { CustomDrawer } from "./components/Main/CustomDrawer";

export const ROWS_PER_PAGE = 50;
export const MOBILE_WIDTH = 600;

export const UserInfoContext = React.createContext({} as any);

function App(): JSX.Element {
  const userContextValue = useUserInfo();
  const [isLogged, setIsLogged] = useState<boolean>(false);
  const [landingPage, setLandingPage] = useState<string | undefined>(undefined);
  const [isDashboardFeatureEnabled] = useFeatureToggle(featureToggleList, Features.DASHBOARD_FEATURE);
  const [isVacationReportEnabled] = useFeatureToggle(featureToggleList, Features.VACATION_REPORT);
  const [isAccomplishmentEnabled] = useFeatureToggle(featureToggleList, Features.ACCOMPLISHMENTS);
  const [isCertificationsEnabled] = useFeatureToggle(featureToggleList, Features.CERTIFICATIONS);

  useEffect(() => {
    authService.redirectUserIfNeededDependingOnLoginState();

    const error = authService.loginErrorJustHappened();

    if (error === LoginError.NOT_AUTHORIZED) {
      alert("You are not authorized");
    }

    // Observers events
    authService.subscribe(AuthEvents.LOGOUT, () => {
      localStorage.removeItem(STORAGE_KEYS.STORED_ASSIGNATION);
      localStorage.removeItem(STORAGE_KEYS.STORED_PROJECT);
      localStorage.removeItem(STORAGE_KEYS.STORED_DATE);
      setIsLogged(false);
    });

    NotificationService.subscribe(NotificationEvents.ERROR, (message: string) => {
      const snackData: SnackBarData = { text: message, severity: "error" };
      sendSnackBarMessage(snackData);
    });

    NotificationService.subscribe(NotificationEvents.INFO, (message: string) => {
      const snackData: SnackBarData = { text: message, severity: "success" };
      sendSnackBarMessage(snackData);
    });

    NotificationService.subscribe(NotificationEvents.WARNING, (message: string) => {
      const snackData: SnackBarData = { text: message, severity: "warning" };
      sendSnackBarMessage(snackData);
    });

    OpsUserService.subscribe(UserEvents.UPDATE, (user: User) => {
      let currentUser = AuthService.getUser();
      if (!!currentUser && currentUser?.email === userContextValue.user?.email) {
        currentUser.avatarUrl = userContextValue.user.avatarUrl;
        currentUser.email = userContextValue.user.email;
        currentUser.firstName = userContextValue.user.firstName;
        currentUser.lastName = userContextValue.user.lastName;
        currentUser.phone = userContextValue.user.phone;
        AuthService.storeUser(currentUser);
        userContextValue.updateLoggedUser(currentUser);
      }
    });

    return () => {
      authService.unsubscribe(AuthEvents.LOGOUT, () => { });
      NotificationService.unsubscribe(NotificationEvents.ERROR, () => { })
      NotificationService.unsubscribe(NotificationEvents.INFO, () => { })
      NotificationService.unsubscribe(NotificationEvents.WARNING, () => { })
      OpsUserService.unsubscribe(UserEvents.UPDATE, () => { });
    }
  }, []);

  useEffect(() => {
    setIsLogged(authService.isLogged());
 
    userContextValue.updateLoggedUser(authService.getUser());
  }, [isLogged]);

  useEffect(() => {
    if (userContextValue.user?.externalId) {
      setLandingPage(getLandingPageByUser(userContextValue.user));
    }
  }, [userContextValue.user]);


  return (
    <Router>
      <ThemeProviderV4 theme={themeV4}>
        <ThemeProviderV5 theme={themeV5}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <UserInfoContext.Provider value={userContextValue}>
              <AppContainer
                key={userContextValue.user?.externalId}
                isLogged={isLogged}
                staticIconsActions={STATIC_ICONS}
                customSections={
                  // user ? getSections(user, isDashboardFeatureEnabled, isVacationReportEnabled, isAccomplishmentEnabled, isCertificationsEnabled) : undefined
                  userContextValue.user?.externalId ? getSections(userContextValue.user, isDashboardFeatureEnabled, isVacationReportEnabled, isAccomplishmentEnabled, isCertificationsEnabled) : undefined
                }
                backgroundColor ={(theme: Theme) => theme.palette.common.white}
                barName={""}
                responsive
                containerSize={"xl"}
                openDrawer
                showAvatarInAppDrawer
                accordionSx={{ whiteSpace: "pre-wrap", textIndent: 4 }}
                stylesForActiveRoute={{
                  color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING,
                }}
                CustomTopBarO = {ToolBar}
                CustomDrawerO = {CustomDrawer}
              >
                <AppNavigation isLogged={isLogged} landingPage={landingPage} />
                <SnackbarComponent />
              </AppContainer>

            </UserInfoContext.Provider>
          </MuiPickersUtilsProvider>
        </ThemeProviderV5>
      </ThemeProviderV4>
    </Router>
  );
}

export default App;
